// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Loader2, Trash2, TriangleAlert } from "lucide-react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useDeletePiiMarkerMaskMutation } from "@/api/hooks/use-pii-marker";
import { WizardPane } from "@/components/shared/wizard-pane";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export interface PiiMarkerDeleteTarget {
  cameraId: number;
  label: string;
}

interface PiiMarkerDeleteDialogProps {
  target: PiiMarkerDeleteTarget | null;
  onClose: () => void;
}

/** Confirms removing a camera's mask row; unlike deactivating, the regions are gone. */
export function PiiMarkerDeleteDialog({ target, onClose }: PiiMarkerDeleteDialogProps) {
  const { t } = useTranslation("back-office");
  // Keep showing the last target while the dialog animates out
  const last = useRef(target);
  if (target) last.current = target;
  const display = target ?? last.current;
  const deleteMask = useDeletePiiMarkerMaskMutation();

  async function handleDelete() {
    if (!display) return;
    try {
      await deleteMask.mutateAsync(display.cameraId);
      toast.success(t("piiMarker.actions.deleted", { name: display.label }));
      onClose();
    } catch {
      // Error toast handled by API middleware
    }
  }

  return (
    <Dialog open={!!target} onOpenChange={() => !deleteMask.isPending && onClose()}>
      <DialogContent id="dialog-delete-pii-mask" className="sm:max-w-[33rem] p-0 gap-0 overflow-hidden flex flex-col">
        <div className="flex flex-row">
          <WizardPane variant="destructive" icon={Trash2} />
          <div className="flex-1 p-6 flex flex-col gap-4 min-w-0">
            <DialogHeader>
              <DialogTitle>{t("piiMarker.actions.deleteTitle")}</DialogTitle>
              <DialogDescription asChild>
                <div>
                  <p>
                    {t("piiMarker.actions.deletePrompt")}
                    <br />
                    <span className="font-semibold font-mono text-foreground">{display?.label}</span>?
                  </p>
                  <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
                    <TriangleAlert className="h-4 w-4 shrink-0 mt-0.5" />
                    <span className="text-sm">{t("piiMarker.actions.deleteWarning")}</span>
                  </div>
                </div>
              </DialogDescription>
            </DialogHeader>
          </div>
        </div>
        <DialogFooter className="mx-0 mb-0 rounded-none rounded-b-lg">
          <Button variant="outline" onClick={onClose} disabled={deleteMask.isPending}>
            {t("buttons.cancel")}
          </Button>
          <Button variant="destructive" onClick={handleDelete} disabled={deleteMask.isPending}>
            {deleteMask.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            {t("piiMarker.actions.delete")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
